import React from 'react';
import { Defs, LinearGradient, Path, StyleSheet, Stop, Svg, Text, View } from '@react-pdf/renderer';
import { PDF_COLORS, PDF_CONTENT_WIDTH, PDF_CONTENT_WIDTH_LANDSCAPE } from './reportPdfTheme';

const styles = StyleSheet.create({
  section: {
    marginBottom: 12,
    padding: 18,
    borderWidth: 1,
    borderColor: PDF_COLORS.stroke,
    borderRadius: 8,
    backgroundColor: PDF_COLORS.white,
  },
  title: { fontSize: 22, fontWeight: 'bold', color: PDF_COLORS.grey900, lineHeight: 1.1 },
  titleAccent: { color: PDF_COLORS.primary },
  // Odpowiednik blockquote ze stroną: kreska z lewej i węższa kolumna tekstu.
  subtitle: {
    width: '55%',
    marginTop: 10,
    paddingLeft: 8,
    borderLeftWidth: 3,
    borderLeftColor: PDF_COLORS.secondary,
    fontSize: 8,
    color: PDF_COLORS.grey600,
    lineHeight: 1.5,
  },
  subtitleWide: { width: '100%' },
  header: { marginBottom: 16 },
  panel: {
    padding: 14,
    borderWidth: 1,
    borderColor: PDF_COLORS.stroke,
    borderRadius: 8,
  },
  panelTitle: { fontSize: 11, fontWeight: 'bold', color: PDF_COLORS.grey900 },
  panelDesc: { marginTop: 3, marginBottom: 12, fontSize: 8, color: PDF_COLORS.grey600 },
  split: { flexDirection: 'row', gap: 20 },
  splitMain: { width: '38%' },
  splitAside: { flex: 1, justifyContent: 'center' },
  track: { height: 10, borderRadius: 5, backgroundColor: PDF_COLORS.grey60 },
});

interface ReportPdfSectionProps {
  title: string;
  titleAccent?: string;
  subtitle?: string;
  aside?: React.ReactNode;
  /**
   * Pozwala sekcji przejść na następną stronę w środku. Domyślnie `false` — slajd ma trzymać się
   * w całości, jak na stronie. `true` jest dla sekcji, których wysokość zależy od danych (tabela
   * z wierszem na temat): przy `wrap={false}` react-pdf ucina to, co nie zmieści się na stronie.
   */
  breakable?: boolean;
  children: React.ReactNode;
}

/** Karta slajdu — ten sam szkielet co `ReportSection` na stronie. */
export const ReportPdfSection: React.FC<ReportPdfSectionProps> = ({
  title,
  titleAccent,
  subtitle,
  aside,
  breakable = false,
  children,
}) => {
  const head = (
    <View style={styles.header}>
      <Text style={styles.title}>
        {title}
        {titleAccent ? '\n' : ''}
        {titleAccent && <Text style={styles.titleAccent}>{titleAccent}</Text>}
      </Text>
      {subtitle && <Text style={[styles.subtitle, aside ? styles.subtitleWide : {}]}>{subtitle}</Text>}
    </View>
  );

  return (
    <View style={styles.section} wrap={breakable}>
      {aside ? (
        <View style={styles.split}>
          <View style={styles.splitMain}>
            {head}
            {children}
          </View>
          <View style={styles.splitAside}>{aside}</View>
        </View>
      ) : (
        <>
          {head}
          {children}
        </>
      )}
    </View>
  );
};

/** Nagłówek bloku metryk — używany też samodzielnie, gdy blok zajmuje całą sekcję bez ramki. */
export const ReportPdfPanelHeading: React.FC<{ title: string; desc: string }> = ({ title, desc }) => (
  <>
    <Text style={styles.panelTitle}>{title}</Text>
    <Text style={styles.panelDesc}>{desc}</Text>
  </>
);

export const ReportPdfPanel: React.FC<{
  title: string;
  desc: string;
  children: React.ReactNode;
}> = ({ title, desc, children }) => (
  <View style={styles.panel}>
    <ReportPdfPanelHeading title={title} desc={desc} />
    {children}
  </View>
);

const BAR_HEIGHT = 10;

/** Poniżej tej szerokości pasek byłby kreską nie do zauważenia — a wartość > 0 ma być widoczna. */
const MIN_VISIBLE_BAR = 2;

/**
 * Ścieżka kapsuły: prostokąt z półokrągłymi końcami. `Rect` w react-pdf ignoruje `rx` (sprawdzone
 * w kodzie pakietu), więc zaokrąglenie rysujemy sami.
 *
 * Promień ograniczamy połową szerokości — przy wąskim wypełnieniu (kilka procent na 70-punktowym
 * pasku w kaflu) pełne zaokrąglenie rozdymało je do średnicy paska, więc 2% wyglądało jak 14%.
 */
const capsulePath = (width: number, height: number): string => {
  const r = Math.min(height / 2, width / 2);

  return `M${r},0 L${width - r},0 A${r},${r} 0 0 1 ${
    width - r
  },${height} L${r},${height} A${r},${r} 0 0 1 ${r},0 Z`;
};

/**
 * Pasek postępu. Gradient rysujemy SVG-iem, bo react-pdf nie zna `linear-gradient` w tle widoku —
 * to jedyny sposób, żeby wykres wyglądał jak na stronie.
 */
export const ReportPdfBar: React.FC<{
  value: number;
  width: number;
  solid?: string;
  /** Wysokość rowka w punktach. Domyślnie 10 (paski sekcyjne); kafle źródeł mają własny, niższy. */
  height?: number;
}> = ({ value, width, solid, height = BAR_HEIGHT }) => {
  const filled = Math.max(Math.min(value, 100), 0);
  const rawWidth = (width * filled) / 100;
  const filledWidth = rawWidth > 0 ? Math.max(rawWidth, MIN_VISIBLE_BAR) : 0;
  // Identyfikator musi być unikalny w obrębie dokumentu — inaczej paski dzielą jeden gradient.
  const gradientId = `bar-${Math.round(value * 100)}-${Math.round(width)}`;

  return (
    // Tło ma DOKŁADNIE tę samą szerokość co rysowany SVG; z `flex` rozciągało się szerzej i pełne
    // 100% nie sięgało końca rowka.
    <View style={[styles.track, { width, height, borderRadius: height / 2 }]}>
      <Svg width={width} height={height}>
        {!solid && (
          <Defs>
            <LinearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
              <Stop offset="0" stopColor={PDF_COLORS.primary} />
              <Stop offset="1" stopColor={PDF_COLORS.secondary} />
            </LinearGradient>
          </Defs>
        )}
        {filledWidth > 0 && (
          <Path d={capsulePath(filledWidth, height)} fill={solid ?? `url(#${gradientId})`} />
        )}
      </Svg>
    </View>
  );
};

export const PDF_INNER_WIDTH = PDF_CONTENT_WIDTH - 36;
/** To samo dla karty poziomej — `styles.section` ma ten sam padding niezależnie od orientacji. */
export const PDF_INNER_WIDTH_LANDSCAPE = PDF_CONTENT_WIDTH_LANDSCAPE - 36;
